import React from 'react';
import { arrayOf, bool, number, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './BlogIndexPage.module.css';

const BLOG_TITLE = 'Rennzone Blog — Porsche Buyer\'s Guides, Market Insight & News';
const BLOG_DESCRIPTION =
  'Porsche buying guides, market reports, and ownership advice from Rennzone — the global Porsche marketplace.';

const decodeEntities = (str = '') =>
  str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#8217;/g, '’')
    .replace(/&#8216;/g, '‘')
    .replace(/&#8220;/g, '“')
    .replace(/&#8221;/g, '”')
    .replace(/&#8211;/g, '–')
    .replace(/&#8212;/g, '—')
    .replace(/&nbsp;/g, ' ');

const stripTags = (html = '') => html.replace(/<[^>]*>/g, '');

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PostCard = ({ post }) => {
  const title = decodeEntities(post.title);
  const excerpt = decodeEntities(stripTags(post.excerptHtml)).trim();
  const category = post.categories && post.categories[0];
  const dateLabel = formatDate(post.date);
  const href = `/blog/${post.slug}`;

  return (
    <a className={css.card} href={href} aria-label={title}>
      <div className={css.cardImageWrap}>
        {post.featuredImage ? (
          <img
            className={css.cardImage}
            src={post.featuredImage.source}
            alt={post.featuredImage.alt || title}
            loading="lazy"
          />
        ) : (
          <div className={css.cardImagePlaceholder} />
        )}
      </div>
      <div className={css.cardBody}>
        <div className={css.cardMeta}>
          {category && (
            <span className={css.cardCategory}>{decodeEntities(category.name)}</span>
          )}
          {category && dateLabel && <span aria-hidden="true">·</span>}
          {dateLabel && <span>{dateLabel}</span>}
        </div>
        <h2 className={css.cardTitle}>{title}</h2>
        {excerpt && <p className={css.cardExcerpt}>{excerpt}</p>}
        <span className={css.cardReadMore}>Read more →</span>
      </div>
    </a>
  );
};

PostCard.propTypes = {
  post: object.isRequired,
};

export const BlogIndexPageComponent = (props) => {
  const {
    posts,
    page,
    totalPages,
    loadInProgress,
    loadError,
    scrollingDisabled,
  } = props;

  const location = useLocation();

  // ItemList JSON-LD for the blog index — helps Google understand the listing.
  const rootURL = process.env.REACT_APP_MARKETPLACE_ROOT_URL || '';
  const itemListSchema =
    posts && posts.length > 0
      ? {
          '@type': 'ItemList',
          itemListElement: posts.map((p, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            url: `${rootURL}/blog/${p.slug}`,
            name: decodeEntities(p.title),
          })),
        }
      : null;

  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${rootURL}/`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${rootURL}/blog`,
      },
    ],
  };

  const schemas = [breadcrumbSchema, itemListSchema].filter(Boolean);

  const prevHref =
    page > 1 ? (page - 1 === 1 ? '/blog' : `/blog?page=${page - 1}`) : null;
  const nextHref =
    totalPages && page < totalPages ? `/blog?page=${page + 1}` : null;

  return (
    <Page
      title={BLOG_TITLE}
      description={BLOG_DESCRIPTION}
      schema={schemas}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <div className={css.root}>
          <div className={css.breadcrumb}>
            <div className={css.breadcrumbInner}>
              <a href="/" className={css.breadcrumbLink}>Home</a>
              <span className={css.breadcrumbSep}>›</span>
              <span className={css.breadcrumbCurrent}>Blog</span>
            </div>
          </div>

          <div className={css.hero}>
            <div className={css.heroInner}>
              <h1 className={css.h1}>Rennzone Blog</h1>
              <p className={css.heroSubtitle}>
                Porsche buyer's guides, market insight, ownership advice, and
                news for collectors and enthusiasts.
              </p>
            </div>
          </div>

          <div className={css.body}>
            <div className={css.bodyInner}>
              {loadInProgress && (!posts || posts.length === 0) && (
                <div className={css.loading}>Loading posts…</div>
              )}

              {loadError && (!posts || posts.length === 0) && (
                <div className={css.error}>
                  We couldn't load blog posts right now. Please try again shortly.
                </div>
              )}

              {!loadInProgress && !loadError && posts && posts.length === 0 && (
                <div className={css.empty}>No blog posts yet — check back soon.</div>
              )}

              {posts && posts.length > 0 && (
                <>
                  <div className={css.grid}>
                    {posts.map((p) => (
                      <PostCard key={p.id} post={p} />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className={css.pagination}>
                      {prevHref ? (
                        <a className={css.paginationLink} href={prevHref}>
                          ← Previous
                        </a>
                      ) : (
                        <span
                          className={`${css.paginationLink} ${css.paginationDisabled}`}
                        >
                          ← Previous
                        </span>
                      )}
                      <span className={css.paginationStatus}>
                        Page {page} of {totalPages}
                      </span>
                      {nextHref ? (
                        <a className={css.paginationLink} href={nextHref}>
                          Next →
                        </a>
                      ) : (
                        <span
                          className={`${css.paginationLink} ${css.paginationDisabled}`}
                        >
                          Next →
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

BlogIndexPageComponent.propTypes = {
  posts: arrayOf(object),
  page: number,
  totalPages: number,
  loadInProgress: bool,
  loadError: object,
  scrollingDisabled: bool,
};

const mapStateToProps = (state) => {
  const { posts, page, totalPages, loadInProgress, loadError } =
    state.BlogIndexPage || {};
  return {
    posts: posts || [],
    page: page || 1,
    totalPages: totalPages || 0,
    loadInProgress: !!loadInProgress,
    loadError: loadError || null,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const BlogIndexPage = compose(connect(mapStateToProps))(BlogIndexPageComponent);

export default BlogIndexPage;
